<template>
  <TransitionRoot as="template" :show="!!props.open">
    <Dialog as="div" class="relative z-50 select-none" @close="close">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div
          class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel class="relative transform">
              <div
                v-show="mediaLoaded"
                class="overflow-hidden rounded-md bg-white text-left shadow-xl transition-all sm:w-full sm:max-w-xl"
              >
                <Media
                  :src="url"
                  :type="mime_type"
                  controls
                  preload="auto"
                  @loaded="handleMediaLoaded"
                />
              </div>
              <LoadingSpinner v-show="!mediaLoaded" class="!w-16 !h-16" />

              <!-- Controls -->
              <template v-if="mediaLoaded">
                <!-- Only show arrows when there are multiple items -->
                <template v-if="multiple">
                  <div
                    class="control translate-y-1/2 left-1 sm:right-full sm:left-auto"
                  >
                    <!-- Move icon 1px over to the left for visual balance -->
                    <ChevronLeftIcon
                      class="relative right-[1px]"
                      @click="previous"
                    />
                  </div>
                  <div
                    class="control translate-y-1/2 right-1 sm:left-full sm:right-auto"
                  >
                    <!-- Move icon 1px over to the right for visual balance -->
                    <ChevronRightIcon
                      class="relative left-[1px]"
                      @click="next"
                    />
                  </div>
                </template>
                <XMarkIcon
                  class="absolute top-2 right-2 p-1 w-8 h-8 text-white hover:bg-gray-300 hover:bg-opacity-40 cursor-pointer rounded-full"
                  @click="close"
                />
              </template>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { defineEmits, defineProps, onUnmounted, ref, watch } from "vue";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/vue/20/solid";
import LoadingSpinner from "@/Tailwind/Components/LoadingSpinner.vue";
import { onKeyStroke } from "@vueuse/core";
import Media from "@/Tailwind/Components/Media.vue";

const props = defineProps({
  open: Boolean,
  url: String,
  mime_type: String,
  multiple: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close", "next", "previous"]);

const mediaLoaded = ref(false);

watch(
  () => [props.open, props.url],
  () => {
    if (props.open) {
      mediaLoaded.value = false;
    }
  }
);

const close = () => {
  emit("close");
};

const next = () => {
  emit("next");
};

const previous = () => {
  emit("previous");
};

const keyStrokeLeftEvent = onKeyStroke(["ArrowLeft"], () => {
  if (props.open) {
    previous();
  }
});

const keyStrokeRightEvent = onKeyStroke(["ArrowRight"], () => {
  if (props.open) {
    next();
  }
});

const handleMediaLoaded = () => {
  mediaLoaded.value = true;
};

onUnmounted(() => {
  keyStrokeLeftEvent();
  keyStrokeRightEvent();
});
</script>

<style lang="scss" scoped>
.control {
  @apply grid place-items-center absolute top-1/2 w-8 text-white hover:bg-gray-300 bg-gray-300 sm:bg-transparent bg-opacity-40 hover:bg-opacity-40 cursor-pointer rounded-full mx-2;
}
</style>
